'use client';

import React from 'react';

import { Button, type ButtonProps } from '@eliadmin/core/src/components/ui/button';
import { useCarouselContext } from '@eliadmin/core/src/components/ui/carousel-context';
import { UI_CONFIG } from '@eliadmin/core/src/constants/ui-config';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { cn, mergeClassObjects } from '@eliadmin/core/src/utils/style-utils';

import { RiArrowLeftSLine, RiArrowRightSLine } from 'react-icons/ri';

export type CarouselControlsProps = {
  className?: {
    main?: string;
    prev?: string;
    next?: string;
    button?: string;
  };
  buttonProps?: ButtonProps;
  prevIcon?: React.ReactNode;
  nextIcon?: React.ReactNode;
};

export const CarouselControls = (props: CarouselControlsProps) => {
  const locale = useLocaleStore(s => s.currentLocale);
  const labels = locale === 'en'
    ? ['Previous slide', 'Next slide']
    : locale === 'cs'
      ? ['Předchozí snímek', 'Další snímek']
      : ['Predchádzajúci snímok', 'Nasledujúci snímok'];
  const className = mergeClassObjects(props.className, UI_CONFIG.carouselControls?.className);
  const buttonProps = props.buttonProps ?? UI_CONFIG.carouselControls?.buttonProps;
  const prevIcon = props.prevIcon ?? UI_CONFIG.carouselControls?.prevIcon ?? (
    <RiArrowLeftSLine size={20} />
  );
  const nextIcon = props.nextIcon ?? UI_CONFIG.carouselControls?.nextIcon ?? (
    <RiArrowRightSLine size={20} />
  );

  const context = useCarouselContext();
  if (!context) throw new Error('CarouselControls must be used inside CarouselProvider');

  if (!context.canGoPrev && !context.canGoNext) return null;

  return (
    <div className={cn('flex flex-row gap-2', 'ea-carousel-controls', className.main)}>
      <Button
        {...buttonProps}
        aria-label={labels[0]}
        onClick={() => context.api.scrollPrev()}
        disabled={!context.canGoPrev}
        className={cn(
          'max-h-12 min-h-12 min-w-12 max-w-12 cursor-pointer rounded-[8px] border-none',
          'ea-carousel-controls__button ea-carousel-controls__button--prev',
          className.button,
          className.prev,
        )}
        variant="dark"
        size="icon"
      >
        {prevIcon}
      </Button>
      <Button
        {...buttonProps}
        aria-label={labels[1]}
        onClick={() => context.api.scrollNext()}
        disabled={!context.canGoNext}
        className={cn(
          'max-h-12 min-h-12 min-w-12 max-w-12 cursor-pointer rounded-[8px] border-none',
          'ea-carousel-controls__button ea-carousel-controls__button--next',
          className.button,
          className.next,
        )}
        variant="dark"
        size="icon"
      >
        {nextIcon}
      </Button>
    </div>
  );
};
